import { TableUiSchema } from './types-v4'
import { JsonFormPropertyV2, JsonFormSchemaV2 } from '../FormComponent/types'

/**
 * Converts a TableUiSchema to a JsonFormSchemaV2 for use in forms
 * @param tableSchema The table schema to convert
 * @param defaultValues Optional default values for the form fields
 * @returns A JsonFormSchemaV2 that can be used with the FormComponent
 */
export const tableToFormSchema = (
  tableSchema: TableUiSchema,
  defaultValues?: Record<string, unknown>
): JsonFormSchemaV2 => {
  // Extract editable columns from the table schema
  const editableColumns = tableSchema.columns.filter(
    (column) =>
      column.columnConfig.editing?.editable !== false && column.columnConfig.display?.component !== 'ProgressStatus'
  )

  // Convert table columns to form properties
  const formProperties: JsonFormPropertyV2[] = editableColumns
    .map((column) => {
      const editing = column.columnConfig.editing

      // Skip columns without editing configuration
      if (!editing) {
        return null
      }

      // Create the form property from the column definition
      const formProperty: JsonFormPropertyV2 = {
        id: column.id,
        title: column.title,
        type: editing.type || 'string',
        description: column.description,
        readOnly: editing.readOnly || false,
        widget: editing.widget,
        required: editing.required,
        fieldLabels: editing.fieldLabels,
        isMultiSelect: editing.isMultiSelect,
        options: editing.options,
        items: editing.items,
        choices: editing.choices,
        format: editing.format,
        minimum: editing.minimum,
        maximum: editing.maximum,
        minLength: editing.minLength,
        maxLength: editing.maxLength,
        pattern: editing.pattern,
        requireWhen: editing.requireWhen,
        validation: editing.validation,
        visible: column.columnConfig.editing?.visible !== false,
        statusIndicator: editing.statusIndicator,
      }

      // Add default value if provided
      if (defaultValues && defaultValues[column.id] !== undefined) {
        if (isTitleValueObject(defaultValues[column.id])) {
          if (formProperty.type === 'string') {
            formProperty.default = (defaultValues[column.id] as { value: string }).value
          } else {
            formProperty.default = (defaultValues[column.id] as { value: number }).value
          }
        } else {
          formProperty.default = defaultValues[column.id]
        }
      } else if (editing.default !== undefined) {
        formProperty.default = editing.default
      }

      return formProperty
    })
    .filter(Boolean) as JsonFormPropertyV2[]

  // Get required fields
  const requiredFields = editableColumns
    .filter((column) => column.columnConfig.editing?.required)
    .map((column) => column.id)

  // Create the form schema
  const formSchema: JsonFormSchemaV2 = {
    type: 'object',
    title: tableSchema.tableConfig.title,
    description: tableSchema.tableConfig.description,
    properties: formProperties,
    required: requiredFields.length > 0 ? requiredFields : undefined,
  }

  return formSchema
}

/**
 * Checks if an object has both 'title' and 'value' properties,
 * where 'title' is a string and 'value' is either a string or a number.
 *
 * @param value - The value to check
 * @returns true if the value matches the criteria, false otherwise
 */
export function isTitleValueObject(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    'title' in value &&
    'value' in value &&
    typeof (value as { title: string }).title === 'string' &&
    (typeof (value as { value: string }).value === 'string' || typeof (value as { value: number }).value === 'number')
  )
}

export function isProgressStatusObject(value: unknown): boolean {
  return typeof value === 'object' && value !== null && 'color' in value && 'marker' in value && 'tooltip' in value
}

export function isProgressStatusArray(value: unknown): boolean {
  return typeof value === 'object' && value !== null && Array.isArray(value) && value.every(isProgressStatusObject)
}
